import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, IconButton, Stack, Tooltip as MuiTooltip, Typography } from '@mui/material';
import { CheckCircle, Refresh, WarningAmber } from '@mui/icons-material';
import RecordsDialog from '../analytics/owner/RecordsDialog';
import { singaporeToday, type DrillSpec } from '../analytics/owner/ownerData';
import {
  ageBucket,
  customerName,
  customerOf,
  failureSummary,
  hourlyToday,
  ingestStatus,
  opsTileDrills,
  queueStatus,
  SLA_TARGET_MINUTES,
  sgtHour,
  whenSgt,
  type OpsHealth,
} from './opsHealth';
import { dayLabel } from './opsHistory';
import { FirstLoad, StaleNotice } from './trackingParts';
import { useRpc } from './useRpc';
import { OpenQueueSection, type QueueRow } from './LiveOpsQueue';
import { FailuresSection, LiveOpsTiles, TodaySection } from './LiveOpsSections';

const REFRESH_MS = 60_000;

/** Polls every minute while the browser tab is visible; on return it refreshes at once. */
function useVisiblePolling(reload: () => void, everyMs: number): boolean {
  const [visible, setVisible] = useState(() => typeof document === 'undefined' || document.visibilityState === 'visible');
  useEffect(() => {
    const onChange = () => {
      const now = document.visibilityState === 'visible';
      setVisible(now);
      if (now) reload();
    };
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, [reload]);
  useEffect(() => {
    if (!visible) return undefined;
    const timer = setInterval(reload, everyMs);
    return () => clearInterval(timer);
  }, [visible, reload, everyMs]);
  return visible;
}

const LiveOpsTab: React.FC = () => {
  const { data, error, loading, reload } = useRpc<OpsHealth>('get_ops_health', null);
  const [now, setNow] = useState(() => new Date());
  const [drill, setDrill] = useState<DrillSpec | null>(null);
  useVisiblePolling(reload, REFRESH_MS);

  // Waits are worked out against the time the figures arrived (and the clock while they are shown).
  useEffect(() => setNow(new Date()), [data, error]);

  const target = data?.sla.clock?.target_minutes ?? SLA_TARGET_MINUTES;
  const queue = useMemo<QueueRow[]>(
    () =>
      (data?.open_rfqs.items ?? []).map((r) => {
        const status = queueStatus(new Date(r.received_at), now, target);
        const key = customerOf(r);
        return { ...r, ...status, customer: key, customerLabel: customerName(key, r.customer_name), bucket: ageBucket(status.waited) };
      }),
    [data, now, target],
  );

  if (!data) return <FirstLoad error={error} what="the live status" onRetry={reload} tiles={5} />;

  const open = queue.filter((r) => r.status === 'NEW');
  const manual = queue.filter((r) => r.status === 'DIY');
  const overdue = open.filter((r) => r.state === 'overdue').length;
  const backlog = open.filter((r) => r.state === 'after_hours');
  const portalCount = data.open_rfqs.open_portal ?? open.filter((r) => r.portal).length;
  const emailCount = data.open_rfqs.open_email ?? open.length - portalCount;

  const today = singaporeToday(now);
  const drills = opsTileDrills(today);
  const ingest = ingestStatus(data.ingest, now);
  const clock = data.sla.clock;
  const failures = failureSummary(data);
  const hourly = hourlyToday(data.today, sgtHour(now));
  const daily = (clock?.daily ?? []).map((d) => ({
    label: dayLabel(d.day),
    within_1h: d.speed.pct_within_1h,
    within_4h: d.speed.pct_within_4h,
    answered: d.speed.answered,
    median: d.speed.median_minutes,
  }));

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" aria-live="polite">
          Updated {whenSgt(data.generated_at)} (Singapore time) · refreshes every minute while this page is open
        </Typography>
        <MuiTooltip title="Refresh now">
          <span>
            <IconButton size="small" onClick={reload} disabled={loading} aria-label="Refresh now">
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </MuiTooltip>
      </Stack>

      <StaleNotice error={error} onRetry={reload} />

      <Alert severity={ingest.level === 'ok' ? 'success' : 'warning'} icon={ingest.level === 'ok' ? <CheckCircle /> : <WarningAmber />} sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {ingest.message}
        </Typography>
        <Typography variant="body2">
          Last email processed {whenSgt(data.ingest.last_email_processed_at)} · last RFQ created {whenSgt(data.ingest.last_rfq_received_at)} · Sent folder last checked{' '}
          {whenSgt(data.ingest.last_sent_folder_sync_at, 'not on record')}
        </Typography>
      </Alert>

      <LiveOpsTiles
        data={data}
        drills={drills}
        emailCount={emailCount}
        portalCount={portalCount}
        overdue={overdue}
        backlogCount={backlog.length}
        failureTotal={failures.total}
        onDrill={setDrill}
      />

      <OpenQueueSection data={data} open={open} manual={manual} backlog={backlog} emailCount={emailCount} portalCount={portalCount} />

      <TodaySection data={data} hourly={hourly} daily={daily} />

      <FailuresSection data={data} />

      <RecordsDialog spec={drill} onClose={() => setDrill(null)} />
    </Box>
  );
};

export default LiveOpsTab;
